'use client'

import cn from 'clsx'
import { useCallback } from 'react'
import { useFormContext } from '../../hook'
import type { InputProps, TextareaProps } from '../../types'

export function Input({
  id,
  name,
  type,
  label,
  className,
  required = false,
  placeholder,
  defaultValue,
  triggerFocus = false,
  validation = () => true,
  ...props
}: InputProps) {
  const { formState, errors, activeFields, register } = useFormContext()

  const validationRule = useCallback(
    (value: string) => {
      if (!required) return true
      return value !== '' && validation(value)
    },
    [validation, required]
  )

  return (
    <div
      className={cn('flex flex-col justify-center dr-gap-2', className)}
      {...props}
    >
      <label htmlFor={id} className="p">
        {label}
      </label>
      <input
        id={id}
        name={name ?? id}
        type={type}
        defaultValue={formState?.inputs?.[id]}
        required={required}
        placeholder={placeholder}
        aria-required={required}
        aria-invalid={errors.get(id)?.state ?? false}
        className={'w-full h-full focus-visible:outline-none'}
        {...register({ id, required, validation: validationRule })}
      />
      {errors.get(id)?.state && activeFields.get(id)?.state && (
        <span className="p text-[var(--color-red)]">
          {errors.get(id)?.message}
        </span>
      )}
    </div>
  )
}

export function Textarea({
  id,
  name,
  label,
  className,
  required = false,
  placeholder,
  defaultValue,
  triggerFocus = false,
  validation = () => true,
  rows = 5,
  ...props
}: TextareaProps) {
  const { formState, errors, activeFields, register } = useFormContext()

  const validationRule = useCallback(
    (value: string) => {
      if (!required) return true
      return value !== '' && validation(value)
    },
    [validation, required]
  )

  return (
    <div
      className={cn('flex flex-col justify-center dr-gap-2', className)}
      {...props}
    >
      <label htmlFor={id} className="p">
        {label}
      </label>
      <textarea
        id={id}
        name={name ?? id}
        required={required}
        placeholder={placeholder}
        defaultValue={formState?.inputs?.[id]}
        rows={rows}
        aria-required={required}
        aria-invalid={errors.get(id)?.state ?? false}
        className={'w-full h-full focus-visible:outline-none'}
        {...register({ id, required, validation: validationRule })}
      />
      {errors.get(id)?.state && activeFields.get(id)?.state && (
        <span className="p text-[var(--color-red)]">
          {errors.get(id)?.message}
        </span>
      )}
    </div>
  )
}
